import { useMemo } from "react";

import { MenuProps } from "antd/es/menu";
import { addDays, addMinutes, isAfter } from "date-fns";
import { useIntl } from "react-intl";

import { SHOULD_USE_12_HOUR_FORMAT } from "@app/util/consts";
import { formatDate } from "@app/util/formatters";

import styles from "./ActivityTime.module.scss";

function* intervalsGenerator(
  startDate: Date,
  intervalSizeInMinutes: number
): Generator<Date, void, never> {
  let date = startDate;
  const nextDay = addDays(startDate, 1);

  while (!isAfter(date, nextDay)) {
    yield date;
    date = addMinutes(date, intervalSizeInMinutes);
  }
}

const useMenu = (
  startDate: Date,
  intervalMinutes: number,
  onChange: (updatedDate: Date) => void,
  currentDate: Date | undefined,
  showInterval?: boolean,
  showNow?: boolean
) => {
  const intl = useIntl();

  const timeFormat = SHOULD_USE_12_HOUR_FORMAT ? "hh:mm a" : "HH:mm";
  const selectedKeys = useMemo(
    () => (currentDate ? [formatDate(currentDate, "PPp")] : []),
    [currentDate]
  );

  const menu: MenuProps = useMemo(
    () => ({
      className: styles.timePickerMenu,
      items: [
        ...Array.from(intervalsGenerator(startDate, intervalMinutes)).map((date, index) => {
          const timeString = formatDate(date, timeFormat);
          const dateString = formatDate(date, "PPp");
          const intervalOffsetMinutes = index * intervalMinutes;

          return {
            key: dateString,
            label: intl.formatMessage(
              {
                id: "createActivityModal.footer.scheduleActivity.time",
                defaultMessage:
                  "{time}{hrs, select, undefined {} other { ({hrs, plural, one {# hr} other {# hrs}})}}",
                description: "Default period time label",
              },
              {
                hrs: showInterval ? intervalOffsetMinutes / 60 : undefined,
                time: timeString,
              }
            ),
            onClick: () => {
              onChange(date);
            },
          };
        }),
        ...(showNow
          ? [
              {
                className: styles.now,
                key: "now",
                label: intl.formatMessage({
                  id: "createActivityModal.footer.scheduleActivity.now",
                  defaultMessage: "Now",
                  description: "Now label",
                }),
                onClick: () => {
                  onChange(new Date());
                },
              },
            ]
          : []),
      ],
      selectedKeys,
    }),
    [intervalMinutes, intl, onChange, selectedKeys, showInterval, showNow, startDate, timeFormat]
  );

  return menu;
};

export default useMenu;
